import RegisterUserPage from "@/pages/RegisterUserPage";
import MyProjectTeamPage from "@/pages/MyProjectTeamPage";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  ThemeProvider,
  CssBaseline,
  CircularProgress,
  Box,
} from "@mui/material";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { theme } from "@/utils/theme";
import { createQueryClient } from "@/utils/queryClient";
import { lazy, Suspense } from "react";

// Eager load critical pages
import LoginPage from "@/pages/LoginPage";

// Lazy load heavy pages
const TimesheetPage = lazy(() => import("@/pages/TimesheetPage"));
const LeaveBalancesPage = lazy(() => import("@/pages/LeaveBalancesPage"));
const LeaveCardPage = lazy(() => import("@/pages/LeaveCardPage"));
const ReportsPage = lazy(() => import("@/pages/ReportsPage"));
const OwnHoursOverviewPage = lazy(() => import("@/pages/OwnHoursOverviewPage"));
const FreeSelectionReportPage = lazy(
  () => import("@/pages/FreeSelectionReportPage")
);
const ManagerWeekOverviewPage = lazy(
  () => import("@/pages/ManagerWeekOverviewPage")
);
const ProjectsPage = lazy(() => import("@/pages/ProjectsPage"));
const NewProjectPage = lazy(() => import("@/pages/NewProjectPage"));
const ClientsManagementPage = lazy(
  () => import("@/pages/ClientsManagementPage")
);
const TaskTypesManagementPage = lazy(
  () => import("@/pages/TaskTypesManagementPage")
);
const ProjectRolesManagementPage = lazy(
  () => import("@/pages/ProjectRolesManagementPage")
);
const ActiveProjectsManagementPage = lazy(
  () => import("@/pages/ActiveProjectsManagementPage")
);
const InactiveProjectsManagementPage = lazy(
  () => import("@/pages/InactiveProjectsManagementPage")
);
const NewUserPage = lazy(() => import("@/pages/NewUserPage"));
const UsersOverviewPage = lazy(() => import("@/pages/UsersOverviewPage"));
const UsersLastLoginPage = lazy(() => import("@/pages/UsersLastLoginPage"));
const ActiveProjectsPage = lazy(() => import("@/pages/ActiveProjectsPage"));
const CompletedProjectsPage = lazy(
  () => import("@/pages/CompletedProjectsPage")
);
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));

// Loading fallback
const PageLoader = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
  >
    <CircularProgress />
  </Box>
);

const queryClient = createQueryClient();

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <TimesheetPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/timesheet"
                  element={
                    <ProtectedRoute>
                      <TimesheetPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/week-overview"
                  element={
                    <ProtectedRoute requireManager>
                      <ManagerWeekOverviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/leave-balances"
                  element={
                    <ProtectedRoute>
                      <LeaveBalancesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/leave-card"
                  element={
                    <ProtectedRoute>
                      <LeaveCardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/my-project-team"
                  element={
                    <ProtectedRoute>
                      <MyProjectTeamPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/active"
                  element={
                    <ProtectedRoute requireManager>
                      <ActiveProjectsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/completed"
                  element={
                    <ProtectedRoute requireManager>
                      <CompletedProjectsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute>
                      <ReportsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/own-hours"
                  element={
                    <ProtectedRoute>
                      <OwnHoursOverviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/free-selection"
                  element={
                    <ProtectedRoute>
                      <FreeSelectionReportPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/project-admin"
                  element={
                    <ProtectedRoute requireManager>
                      <ProjectsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/project-admin/new-project"
                  element={
                    <ProtectedRoute requireManager>
                      <NewProjectPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/project-admin/clients"
                  element={
                    <ProtectedRoute requireManager>
                      <ClientsManagementPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/project-admin/activities"
                  element={
                    <ProtectedRoute requireAdmin>
                      <TaskTypesManagementPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/project-admin/project-roles"
                  element={
                    <ProtectedRoute requireManager>
                      <ProjectRolesManagementPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/project-admin/active-projects"
                  element={
                    <ProtectedRoute requireManager>
                      <ActiveProjectsManagementPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/project-admin/inactive-projects"
                  element={
                    <ProtectedRoute requireManager>
                      <InactiveProjectsManagementPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users/new"
                  element={
                    <ProtectedRoute requireAdmin>
                      <NewUserPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute requireAdmin>
                      <UsersOverviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users/last-login"
                  element={
                    <ProtectedRoute requireAdmin>
                      <UsersLastLoginPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users/register"
                  element={
                    <ProtectedRoute requireAdmin>
                      <RegisterUserPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
